import FindOneProject from "@/v2/api/project/FindOne";
import { useQuery } from "@tanstack/react-query";
import {
    BanknoteIcon,
    BuildingIcon,
    Calendar,
    FileText,
    Hash,
    MapPin,
    User,
    Users,
    CheckCircle,
    XCircle,
    SheetIcon,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import ProjectDeleteButton from "@/v2/components/ui/button/delete/Project";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import ViewLayout from "@/v2/layout/View";
import { CardContent } from "@/components/ui/card";
import AccessControl from "@/v2/components/rbac/AccessControl";
import { Separator } from "@/components/ui/separator";
import EditProject from "./Edit";

const NavButtons = ({ id }: { id: string }) => {
    return (
        <>
            <AccessControl
                module="MOBILIZATION_MANAGEMENT"
                requestedAccess={"PUT"}
            >
                <EditProject id={id} isInside />
            </AccessControl>

            <AccessControl
                module="PROJECT_MANAGEMENT"
                requestedAccess={"DELETE"}
            >
                <ProjectDeleteButton id={id} isInside />
            </AccessControl>
        </>
    );
};

export default function ProjectViewOne() {
    const { id } = useParams();
    const navigate = useNavigate();

    if (!id) navigate(-1);

    const query = useQuery({
        queryKey: ["project", id],
        queryFn: () => FindOneProject(id!),
    });

    if (query.isLoading || query.isFetching) {
        return <LoadingAnimation />;
    }

    if (!query.data) return <p>Something went wrong</p>;

    const projectData = query.data;

    return (
        <ViewLayout
            title="Project Details"
            description="Information about the project"
            icon={FileText}
            buttons={<NavButtons id={id!} />}
        >
            <CardContent className="space-y-8">
                {/* Basic Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Basic Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <FileText className="mr-2 h-4 w-4" />
                                Project Name
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.name || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                Reference Number
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.reference_number || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                PO Number
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.po_number || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <MapPin className="mr-2 h-4 w-4" />
                                Location
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.location || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <SheetIcon className="mr-2 h-4 w-4" />
                                Description
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.description || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                {projectData?.closed ? (
                                    <XCircle className="mr-2 h-4 w-4" />
                                ) : (
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                )}
                                Status
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.closed ? "Closed" : "Active"}
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />
                {/* Timeline */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">Timeline</h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Calendar className="mr-2 h-4 w-4" />
                                Start Date
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.start_date
                                    ? new Date(
                                          projectData.start_date,
                                      ).toLocaleDateString()
                                    : "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Calendar className="mr-2 h-4 w-4" />
                                End Date
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.end_date
                                    ? new Date(
                                          projectData.end_date,
                                      ).toLocaleDateString()
                                    : "N/A"}
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />
                {/* Client Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Client Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <BuildingIcon className="mr-2 h-4 w-4" />
                                Client Name
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.client?.full_name || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Hash className="mr-2 h-4 w-4" />
                                Vendor Code
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.client_vendor_code || "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <MapPin className="mr-2 h-4 w-4" />
                                Client Location
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.client_location || "N/A"}
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />
                {/* Client Representative */}
                {projectData?.client_representative && (
                    <div>
                        <h3 className="mb-4 text-lg font-semibold">
                            Client Representative
                        </h3>
                        <div className="rounded-lg border border-border bg-muted/50 p-4">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="space-y-1">
                                    <div className="flex items-center text-sm text-muted-foreground">
                                        <User className="mr-2 h-4 w-4" />
                                        Name
                                    </div>
                                    <p className="text-base font-medium">
                                        {projectData.client_representative
                                            ?.full_name || "N/A"}
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center text-sm text-muted-foreground">
                                        <User className="mr-2 h-4 w-4" />
                                        Email
                                    </div>
                                    <p className="break-all text-base font-medium">
                                        {projectData.client_representative
                                            ?.email || "N/A"}
                                    </p>
                                </div>
                                <div className="space-y-1">
                                    <div className="flex items-center text-sm text-muted-foreground">
                                        <User className="mr-2 h-4 w-4" />
                                        Phone
                                    </div>
                                    <p className="text-base font-medium">
                                        {projectData.client_representative
                                            ?.phone_number || "N/A"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Separator />
                {/* Financial Information */}
                <div>
                    <h3 className="mb-4 text-lg font-semibold">
                        Financial Information
                    </h3>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <BanknoteIcon className="mr-2 h-4 w-4" />
                                Client Rate
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.client_rate
                                    ? `${projectData.client_rate}`
                                    : "N/A"}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <BanknoteIcon className="mr-2 h-4 w-4" />
                                Client Commission
                            </div>
                            <p className="text-base font-medium">
                                {projectData?.client_commission
                                    ? `${projectData.client_commission}`
                                    : "N/A"}
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />
                {/* AR Coordinator */}
                {projectData?.ar_coordinator && (
                    <div>
                        <h3 className="mb-4 text-lg font-semibold">
                            AR Coordinator
                        </h3>
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Users className="mr-2 h-4 w-4" />
                                Coordinator
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="rounded-md bg-secondary px-3 py-1 text-sm font-medium">
                                    {projectData.ar_coordinator?.full_name ||
                                        "N/A"}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </ViewLayout>
    );
}
